import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  MessageSquare, 
  ImageIcon, 
  Mic, 
  FileText, 
  Globe,
  Zap,
  Sparkles,
  ArrowRight,
  Brain,
  Wand2,
  AudioLines,
  ScanText
} from "lucide-react";
import HelcooLogo from "../components/HelcooLogo";
import { useLanguage } from "../context/LanguageContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.3 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  }
};

export default function LandingPage() {
  const { language, setLanguage, t } = useLanguage();

  const features = [
    { 
      icon: Brain, 
      title: language === "ar" ? "محادثات ذكية" : "Smart Chat",
      desc: language === "ar" ? "تحدث مع أذكى نماذج الذكاء الاصطناعي" : "Chat with the smartest AI models"
    },
    { 
      icon: Wand2, 
      title: language === "ar" ? "توليد الصور" : "Image Generation",
      desc: language === "ar" ? "أنشئ صوراً مذهلة من الكلمات" : "Create stunning images from words"
    },
    { 
      icon: AudioLines, 
      title: language === "ar" ? "تحويل الصوت" : "Voice Conversion",
      desc: language === "ar" ? "حوّل الصوت إلى نص بدقة عالية" : "Convert audio to text accurately"
    },
    { 
      icon: ScanText, 
      title: language === "ar" ? "تلخيص النصوص" : "Text Summary",
      desc: language === "ar" ? "لخّص أي نص بضغطة زر" : "Summarize any text instantly"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Elegant Background */}
      <div className="fixed inset-0 pointer-events-none">
        {/* Subtle gradient */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-foreground/3 rounded-full blur-[150px] translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-foreground/2 rounded-full blur-[120px] -translate-x-1/2 translate-y-1/2" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      {/* Language Switcher */}
      <motion.button
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        onClick={() => setLanguage(language === "ar" ? "en" : "ar")}
        className="fixed top-6 start-6 z-50 flex items-center gap-2.5 px-5 py-3 rounded-2xl bg-secondary hover:bg-secondary/80 transition-all duration-300"
        data-testid="landing-language-switcher"
      >
        <Globe className="w-4 h-4 text-foreground" />
        <span className="text-sm font-semibold">{language === "ar" ? "English" : "العربية"}</span>
      </motion.button>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Logo */}
          <motion.div variants={itemVariants} className="flex justify-center mb-10">
            <motion.div
              className="relative"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <HelcooLogo size={120} animated={true} />
            </motion.div>
          </motion.div>

          {/* Title */}
          <motion.h1 
            variants={itemVariants} 
            className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 tracking-tight text-foreground"
            style={{ fontFamily: 'Cairo, sans-serif' }}
          >
            HELCOO
          </motion.h1>

          {/* Tagline */}
          <motion.p 
            variants={itemVariants} 
            className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-12 leading-relaxed"
          >
            {language === "ar" 
              ? "منصة الذكاء الاصطناعي المتكاملة - محادثات ذكية، توليد صور، وأكثر"
              : "The All-in-One AI Platform - Smart chats, image generation, and more"}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <motion.button 
                className="group relative overflow-hidden px-8 py-4 rounded-2xl bg-foreground text-background font-semibold min-w-[220px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="start-chat-btn"
              >
                {/* Shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-background/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <span className="relative flex items-center justify-center gap-3">
                  <Zap className="w-5 h-5" />
                  {language === "ar" ? "ابدأ المحادثة" : "Start Chatting"}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </motion.button>
            </Link>
            <Link to="/login">
              <motion.button 
                className="px-8 py-4 rounded-2xl bg-secondary border border-border text-foreground font-semibold min-w-[220px] hover:bg-secondary/80 transition-colors duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="create-image-btn"
              >
                <span className="flex items-center justify-center gap-3">
                  <ImageIcon className="w-5 h-5" />
                  {language === "ar" ? "أنشئ صورة" : "Create Image"}
                </span>
              </motion.button>
            </Link>
          </motion.div>
          
          {/* Trust badge */}
          <motion.div 
            variants={itemVariants}
            className="mt-12 flex items-center justify-center gap-2 text-muted-foreground"
          >
            <Sparkles className="w-4 h-4 text-foreground" />
            <span className="text-sm">
              {language === "ar" ? "مدعوم بأحدث نماذج الذكاء الاصطناعي" : "Powered by latest AI models"}
            </span>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-7 h-12 rounded-full border-2 border-border flex items-start justify-center p-2"
          >
            <motion.div
              className="w-1.5 h-3 rounded-full bg-foreground"
              animate={{ y: [0, 16, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-20"
          >
            <span className="inline-block px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6 bg-secondary text-foreground border border-border">
              {language === "ar" ? "الأدوات المتاحة" : "Available Tools"}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {language === "ar" ? "كل ما تحتاجه في مكان واحد" : "Everything you need in one place"}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {language === "ar" 
                ? "استمتع بتجربة فريدة مع أدوات الذكاء الاصطناعي المتقدمة"
                : "Enjoy a unique experience with advanced AI tools"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <Link to="/login" data-testid={`feature-${index}`}>
                  <motion.div 
                    className="feature-card h-full text-center group"
                    whileHover={{ y: -8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-5 mx-auto group-hover:bg-foreground group-hover:text-background transition-colors duration-300">
                      <feature.icon className="w-7 h-7" />
                    </div>
                    <h3 className="font-bold text-lg mb-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {feature.desc}
                    </p>
                  </motion.div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6">
        <motion.div 
          className="max-w-3xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="p-12 rounded-3xl bg-secondary border border-border">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Cairo, sans-serif' }}>
              {language === "ar" ? "ابدأ رحلتك الآن" : "Start your journey now"}
            </h2>
            <p className="text-muted-foreground mb-8">
              {language === "ar" 
                ? "انضم لآلاف المستخدمين الذين يستمتعون بتجربة HELCOO"
                : "Join thousands of users enjoying the HELCOO experience"}
            </p>
            <Link to="/login">
              <motion.button 
                className="px-10 py-4 rounded-2xl bg-foreground text-background font-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {language === "ar" ? "سجّل مجاناً" : "Sign up free"}
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-10 border-t border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <HelcooLogo size={28} animated={false} />
              <span className="font-bold">HELCOO</span>
            </div>
            <p className="text-xs text-muted-foreground">
              © 2026 HELCOO. {language === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved"}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
